import { z } from 'zod';
import { BankAccountSchema, CurrencySchema, MoneySchema, TransactionIdSchema, WithdrawalIdSchema, } from './common.js';
/**
 * `GET /v1/deposit-options`, `POST /v1/deposit-options/reserve` (SAD §5, §8, M-01).
 */
const DECIMAL_STRING_REGEX = /^\d+(\.\d+)?$/;
// ---------------------------------------------------------------------------------------------
// GET /v1/deposit-options
// ---------------------------------------------------------------------------------------------
/** Query parameters for `GET /v1/deposit-options` (SAD §8: `?currency=THB`). */
export const DepositOptionsQuerySchema = z
    .object({
    currency: CurrencySchema,
    /**
     * The asking customer's opaque per-website ref (the same value `reserve` sends as
     * `depositor_account_ref`). Optional; when present, amounts backed only by that customer's
     * own withdrawals are not offered — the no-self-match rule, list side. The claim enforces
     * the same rule at reserve time regardless, so omitting this merely shows amounts that
     * would then 409.
     */
    depositor_account_ref: z.string().min(1).max(255).optional(),
    /**
     * The asking customer's registered bank — symbolic code, same constraints as
     * `BankAccountSchema.bank` (e.g. `BBL`, `GSB`). Optional; when present, amounts backed only
     * by withdrawals at this bank are not offered *if* the bank is on the policy's same-bank
     * blocklist — banks whose same-bank transfer slips carry no QR code and so could never pass
     * verification. The claim enforces the same rule at reserve time from the reservation's
     * `payer_account.bank`, so omitting this merely shows amounts that would then 409.
     */
    payer_bank: z.string().min(2).max(20).optional(),
    /**
     * How many of the longest-waiting withdrawals the caller counts as "the head of the queue".
     *
     * PF has no opinion on what the caller does with the answer — a website running a
     * clear-the-old-queue promotion asks for `1`, and each option comes back saying whether the
     * withdrawal *this* depositor's claim would take is inside that window. The point is that the
     * caller cannot compute it: which row a claim takes depends on the no-self-match and
     * same-bank rules, and those are PF's to apply, not to publish.
     */
    oldest_queue_window: z.coerce.number().int().min(1).max(1000).optional(),
})
    .strict();
/**
 * One amount bucket in the order book — **amounts and availability only, never bank details**
 * (SAD §5: "no bank details until reserved"). `.strict()` is what makes this a compile-time and
 * runtime guarantee: any accidental `bank`/`name`/`number` field on the response fails to parse.
 */
export const DepositOptionSchema = z
    .object({
    amount: z.string().regex(DECIMAL_STRING_REGEX, 'must be a non-negative decimal string'),
    available: z.boolean(),
    /**
     * Whether the withdrawal *this* depositor's claim would take for this amount is among the
     * `oldest_queue_window` longest-waiting AVAILABLE withdrawals in the caller's own pool.
     * Present only when the query asked for a window.
     *
     * A queue position, not a payee: it says nothing about who is being paid, and the depositor's
     * own website already knows its own pool. Under clearing this is `false` for an amount only
     * another website's pool backs — that match clears their queue, not the caller's.
     */
    in_oldest_window: z.boolean().optional(),
})
    .strict();
/**
 * Whether this caller's deposits may be served by *splitting* a larger withdrawal, and within
 * what bounds (migration 0034).
 *
 * Deliberately **policy constants and a capability flag only** — the same value for every caller
 * at this participant, and therefore not order-book shape. In particular there is no
 * "largest amount that could be split", because that is the size of the biggest withdrawal on the
 * book, which is exactly what {@link DepositOptionSchema} exists not to leak. A website that
 * wants a ceiling for a free-amount field already has one: the largest amount in `options`.
 *
 * `min_remainder` is why a free-amount field cannot be validated as a simple interval — the
 * valid set has holes. Asking for `W − 50` against a 5,000 withdrawal is refused when the
 * minimum remainder is 100, and no client-side rule can know that without knowing `W`. The
 * server is the authority; this exists so a website can explain the refusal, not predict it.
 */
export const SplitAvailabilitySchema = z
    .object({
    enabled: z.boolean(),
    min_slice: MoneySchema,
    min_remainder: MoneySchema,
})
    .strict();
/** `200` response for `GET /v1/deposit-options` (SAD §8). No bank details — see `DepositOptionSchema`. */
export const DepositOptionsResponseSchema = z
    .object({
    options: z.array(DepositOptionSchema),
    /** Absent for a participant that is not split-enabled — which is every one by default. */
    split: SplitAvailabilitySchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/deposit-options/reserve
// ---------------------------------------------------------------------------------------------
/**
 * Request body for `POST /v1/deposit-options/reserve` (SAD §8) — an atomic claim of exactly one
 * AVAILABLE withdrawal of the exact amount. `409 OUT_OF_STOCK` if none exists.
 */
export const ReserveDepositOptionRequestSchema = z
    .object({
    client_reference: z.string().min(1).max(255),
    amount: MoneySchema,
    depositor: z
        .object({
        participant: z.string().min(1).max(128),
        account_ref: z.string().min(1).max(255),
        /**
         * The website's *display* handle for this customer — the username an operator
         * recognises. Display-only, never an identity: `account_ref` stays the key everywhere
         * (reputation, no-self-match, history search), and this feeds the `account_directory`
         * sidecar the operator console joins, latest write winning. Optional: a participant
         * that does not send it simply leaves its customers shown by ref.
         */
        account_display: z.string().min(1).max(255).optional(),
    })
        .strict(),
    /**
     * The bank account the participating website's customer has registered — verify-the-sender.
     *
     * Optional: a participant that has not adopted sender verification omits it, and the check
     * stays not-applicable for that reservation (legacy behavior, unchanged). When supplied, the
     * depositor is expected to transfer FROM this account, and a slip whose attested sender
     * disagrees goes to manual review rather than auto-approving (SAD §6 extension).
     */
    payer_account: BankAccountSchema.optional(),
    /**
     * Same meaning as on the listing (see `DepositOptionsQuerySchema`), asked of the withdrawal
     * this call actually claims. Supplying it here is what makes the listing's `in_oldest_window`
     * safe to act on: a caller that badges an amount from the list and then stamps its own verdict
     * from a local copy of the pool can disagree with PF, and the depositor is the one who finds
     * out. Optional — omit it and the response simply carries no verdict.
     */
    oldest_queue_window: z.number().int().min(1).max(1000).optional(),
})
    .strict();
/**
 * `201` response for `POST /v1/deposit-options/reserve` (SAD §8) — reveals the reserved
 * withdrawer's bank details (`pay_to`) now that the claim is exclusive, plus the reservation TTL.
 */
export const ReserveDepositOptionResponseSchema = z
    .object({
    id: TransactionIdSchema,
    status: z.literal('RESERVED'),
    matched_withdrawal_id: WithdrawalIdSchema,
    pay_to: BankAccountSchema,
    /**
     * The account the depositor must transfer FROM — the request's `payer_account`, echoed back
     * so the deposit screen can *tell* the customer which account the sender check will hold
     * them to, instead of failing them after the fact. Omitted when the reservation carried no
     * `payer_account` (the check is then not applicable).
     *
     * `number` is the **masked** form (`PayerAccount.numberMasked`), never the full number: the
     * customer is being reminded of an account they already own, not told one (SAD §5
     * P9-adjacent: minimise exposure — same rule as the ops console's `ExpectedPayerSchema`).
     */
    pay_from: BankAccountSchema.optional(),
    reserved_until: z.string().datetime({ offset: true }),
    /**
     * Whether the withdrawal just claimed was among the `oldest_queue_window` longest-waiting
     * AVAILABLE withdrawals in the caller's own pool, judged at claim time — the moment the
     * queue position means anything. Present only when the request asked for a window, and only
     * on a fresh claim: an idempotent replay returns the original reservation, and re-judging it
     * against a pool that has since moved would answer a different question.
     */
    matched_withdrawal_in_oldest_window: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=deposit-options.js.map